import type { UiConfig } from '../types';
import { shopifyImg } from '../utils/img';

type Moment = NonNullable<UiConfig['moments']>[number];

interface Props {
  moments: Moment[];
  title?: string;
}

/**
 * MomentsSection — customer "moments" / testimonials shown below the upload
 * flow as social proof. Mirrors the reference bf-moments layout: a centered
 * title over a stack of white cards, each card alternating image-left /
 * image-right (bf-moment-even) with a 3:4 portrait, the reviewer's name in
 * brand gold, and their review.
 *
 * Renders nothing when no moments are configured so the upload view stays
 * clean for products without testimonials.
 */
export function MomentsSection({ moments, title = 'Moments That Meant Something' }: Props) {
  const items = moments.filter((m) => m && (m.review || m.name));
  if (items.length === 0) return null;

  return (
    <section className="flex flex-col items-center gap-5 pt-4">
      <h2 className="font-serif text-2xl sm:text-[30px] leading-tight text-warm-brown text-center m-0">
        {title}
      </h2>

      <div className="w-full max-w-[750px] flex flex-col gap-7">
        {items.map((m, i) => {
          const imageLeft = i % 2 === 0; // alternate; even = image left
          const hasImage = m.image && !m.image.includes('<<');
          return (
            <article
              key={`${m.name}-${i}`}
              className={[
                'bf-moment-row bg-white rounded-2xl p-6 border border-soft-border shadow-soft',
                imageLeft ? '' : 'bf-moment-row--rev',
              ].join(' ')}
            >
              {hasImage && (
                <div className="bf-moment-media rounded-[14px] overflow-hidden bg-cream2">
                  <img src={shopifyImg(m.image, 600)} alt={m.name ? `Portrait for ${m.name}` : ''} loading="lazy" decoding="async" />
                </div>
              )}
              <div className="bf-moment-text flex flex-col gap-2">
                <p className="text-warm-brown/90 leading-relaxed text-[15px] m-0">“{m.review}”</p>
                {m.name && <p className="font-semibold text-teal m-0">— {m.name}</p>}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
